import { Calendar, CalendarProps } from 'primereact/calendar';
import { FormEvent } from 'primereact/ts-helpers';
import { classNames } from 'primereact/utils';
import { useRef } from 'react';
import {
  FieldValues,
  useController,
  UseControllerProps,
  UseFormClearErrors,
  UseFormTrigger,
} from 'react-hook-form';

import ErrorMessage from '../../error-message';
import styles from './DatePicker.module.css';

export type DatePickerProps<T extends FieldValues> = Omit<
  CalendarProps,
  'name' | 'value' | 'onChange'
> & {
  label?: string;
  clearErrors?: UseFormClearErrors<T>;
  showError?: boolean;
  trigger?: UseFormTrigger<T>;
  endDateAsLastDayOfYear?: boolean;
  /**
   * Range mode only: derive the end of the range from the picked start date, so a single
   * click completes the range. The input then displays the resulting "start - end".
   */
  deriveRangeEnd?: (start: Date) => Date;
  /**
   * Single mode only: expand the picked date into a [start, end] range stored on the form,
   * while the picker keeps a single selection (it shows/highlights the stored start).
   */
  mapSingleToRange?: (selected: Date) => Date[];
  /** Node rendered inside the input, just left of the calendar trigger (e.g. a tooltip icon). */
  customIcon?: React.ReactNode;
} & UseControllerProps<T>;

const DatePicker = <T extends FieldValues>({
  name,
  control,
  rules,
  defaultValue,
  label,
  className,
  clearErrors,
  showError = true,
  trigger,
  endDateAsLastDayOfYear,
  deriveRangeEnd,
  mapSingleToRange,
  customIcon,
  selectionMode,
  disabled,
  ...props
}: DatePickerProps<T>) => {
  const calendarRef = useRef<Calendar | null>(null);
  const {
    field: { value, ref, onChange, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  // The form value is a [start, end] range while the calendar itself selects a single date.
  const isSingleBackedByRange = Boolean(mapSingleToRange) && selectionMode === 'single';
  const calendarValue = isSingleBackedByRange && Array.isArray(value) ? value[0] : value;

  const handleOnChange = (e: FormEvent<(Date | null)[] | Date>) => {
    const selecetedDates = e.value;

    // Store the whole range the picked date stands for, keeping the single selection intact.
    if (mapSingleToRange && selectionMode === 'single') {
      const selectedDate = selecetedDates as Date | null;

      onChange(selectedDate ? mapSingleToRange(selectedDate) : e);
      clearErrors?.(name);
      trigger?.(name);

      return;
    }

    // Convert the end date to the last day of the year
    // This can happen only when the selection is a range
    if (endDateAsLastDayOfYear && selectionMode === 'range') {
      const endDate = (selecetedDates as Date[])[1];

      if (endDate) {
        endDate.setMonth(11);
        endDate.setDate(31);
      }
    }

    // Derive the range end from the picked start, so one click completes the range.
    if (deriveRangeEnd && selectionMode === 'range') {
      const dates = selecetedDates as (Date | null)[];
      const startDate = dates?.[0];

      if (startDate && !dates[1]) {
        const endDate = deriveRangeEnd(startDate);
        const newDates = [startDate, endDate];

        onChange(newDates);

        clearErrors?.(name);
        trigger?.(name);
        calendarRef.current?.hide();

        return;
      }
    }

    onChange(e);
    clearErrors?.(name);
    trigger?.(name);
  };

  return (
    <div className={classNames('field p-fluid', { [styles.disabled]: disabled })}>
      <span className={classNames('p-float-label', { [styles['has-custom-icon']]: customIcon })}>
        <Calendar
          id={name}
          value={calendarValue}
          ref={(instance) => {
            calendarRef.current = instance;
            if (typeof ref === 'function') {
              ref(instance);
            }
          }}
          onChange={handleOnChange}
          {...props}
          {...inputProps}
          className={classNames(className, {
            'p-invalid': error,
          })}
          showIcon
          readOnlyInput
          disabled={disabled}
          selectionMode={selectionMode}
        />
        {customIcon && <div className={styles['custom-icon']}>{customIcon}</div>}
        {label && <label htmlFor={name}>{label}</label>}
      </span>
      {showError && (
        <div className={classNames(styles['error-message-box'])}>
          {invalid && (
            <ErrorMessage
              message={Array.isArray(error) ? error[1]?.message : error?.message || ''}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
